/**
 * Brown Butters favourites (wishlist).
 *
 * Shopify has no native wishlist, so this is entirely client side: a versioned array of
 * saved products kept in one localStorage key. No app, no customer account, works on the
 * first click. Everything a saved product needs in order to be rendered on the wishlist
 * page is stored alongside the handle, so that page never has to fetch anything.
 *
 * Storage
 *   key    mbb:wishlist:v1
 *   value  { "version": 1, "items": [ { handle, id, variantId, title, url, image, price,
 *                                       addedAt } ] }
 *   The key carries the schema version so the shape can change later without having to
 *   guess at what an old payload meant — an unrecognised version is simply ignored.
 *
 * Custom elements
 *   <mbb-wishlist-toggle>  wraps a real <button>; toggles the product, reflects state on
 *                          aria-pressed, swaps its own icon fill.
 *   <mbb-wishlist-count>   the header badge; hides itself at zero.
 *   <mbb-wishlist-list>    the wishlist page; renders saved items, remove, add all, empty
 *                          state.
 *
 * Every element listens for `mbb:wishlist:change` on `document`, so nothing has to know
 * about anything else. State is re-read on DOMContentLoaded, on `shopify:section:load`
 * (theme editor and Section Rendering API swaps) and on `pageshow` from the back/forward
 * cache, because Shopify pages are cached and the markup that arrives is always the
 * "nothing saved" version.
 *
 * If localStorage is unavailable (private browsing, quota, blocked cookies) nothing
 * throws: the toggles and the badge remove themselves and the page behaves as though the
 * feature were switched off.
 */

const KEY = 'mbb:wishlist:v1';
const SCHEMA_VERSION = 1;
const MAX_ITEMS = 100;
const CHANGE_EVENT = 'mbb:wishlist:change';

/**
 * localStorage can exist and still throw on write (Safari private browsing, full quota,
 * cookies blocked). The only reliable probe is an actual round trip.
 */
function probe() {
  try {
    const store = window.localStorage;
    if (!store) return null;
    const probeKey = `${KEY}:probe`;
    store.setItem(probeKey, '1');
    store.removeItem(probeKey);
    return store;
  } catch {
    return null;
  }
}

const store = probe();
const available = store !== null;

function text(value) {
  return typeof value === 'string' ? value : '';
}

/** Only same-origin or absolute http(s) URLs are ever written into the DOM. */
function safeUrl(value) {
  const raw = text(value).trim();
  if (raw === '') return '';
  if (raw.startsWith('/') || raw.startsWith('https://') || raw.startsWith('http://')) return raw;
  return '';
}

function normalise(input) {
  if (!input || typeof input !== 'object') return null;
  const handle = text(input.handle).trim();
  if (handle === '') return null;
  const added = Number(input.addedAt);
  return {
    handle,
    id: text(input.id),
    variantId: text(input.variantId),
    title: text(input.title),
    url: safeUrl(input.url),
    image: safeUrl(input.image),
    price: text(input.price),
    addedAt: Number.isFinite(added) && added > 0 ? added : Date.now(),
  };
}

function read() {
  if (!available) return [];

  let raw = null;
  try {
    raw = store.getItem(KEY);
  } catch {
    return [];
  }
  if (!raw) return [];

  let parsed = null;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }

  // Tolerate a bare array as well as the versioned envelope: the very first shape shipped
  // may have been either, and a wishlist is not worth losing over a wrapper.
  let list = null;
  if (Array.isArray(parsed)) {
    list = parsed;
  } else if (parsed && typeof parsed === 'object' && Array.isArray(parsed.items)) {
    if (parsed.version !== undefined && Number(parsed.version) > SCHEMA_VERSION) return [];
    list = parsed.items;
  }
  if (!list) return [];

  const seen = new Set();
  const out = [];
  for (const entry of list) {
    const item = normalise(entry);
    if (!item || seen.has(item.handle)) continue;
    seen.add(item.handle);
    out.push(item);
    if (out.length >= MAX_ITEMS) break;
  }
  return out;
}

function write(items) {
  if (!available) return false;
  try {
    store.setItem(KEY, JSON.stringify({ version: SCHEMA_VERSION, items }));
    return true;
  } catch {
    // Quota or a blocked write. The in-memory list still reflects the click so the page
    // stays coherent for this session; it simply will not survive a reload.
    return false;
  }
}

let cache = read();

function items() {
  return cache.slice();
}

function count() {
  return cache.length;
}

function has(handle) {
  const key = text(handle).trim();
  if (key === '') return false;
  return cache.some((item) => item.handle === key);
}

function announce(detail) {
  document.dispatchEvent(
    new CustomEvent(CHANGE_EVENT, {
      bubbles: false,
      detail: { items: items(), count: cache.length, available, ...detail },
    })
  );
}

function commit(next, detail) {
  cache = next;
  const persisted = write(cache);
  announce({ persisted, ...detail });
  return persisted;
}

function add(input) {
  const item = normalise(input);
  if (!item) return false;
  const next = cache.filter((entry) => entry.handle !== item.handle);
  next.unshift(item);
  commit(next.slice(0, MAX_ITEMS), { reason: 'add', handle: item.handle, active: true });
  return true;
}

function remove(handle) {
  const key = text(handle).trim();
  if (key === '') return false;
  const next = cache.filter((entry) => entry.handle !== key);
  if (next.length === cache.length) return false;
  commit(next, { reason: 'remove', handle: key, active: false });
  return true;
}

function toggle(input) {
  const item = normalise(input);
  if (!item) return false;
  if (has(item.handle)) {
    remove(item.handle);
    return false;
  }
  add(item);
  return true;
}

function clear() {
  if (cache.length === 0) return;
  commit([], { reason: 'clear' });
}

/** Re-read storage and tell every element about it. */
function hydrate(reason) {
  cache = read();
  announce({ reason: reason || 'hydrate' });
}

/* ------------------------------------------------------------------ elements */

class WishlistToggle extends HTMLElement {
  #onChange = () => this.sync();

  connectedCallback() {
    if (!available) {
      this.hidden = true;
      return;
    }

    const button = this.button;
    if (!button) return;

    if (button.dataset.mbbWishBound !== 'true') {
      button.dataset.mbbWishBound = 'true';
      button.addEventListener('click', (event) => {
        // The heart usually sits on top of a card-wide product link.
        event.preventDefault();
        event.stopPropagation();
        toggle(this.item);
      });
    }

    document.addEventListener(CHANGE_EVENT, this.#onChange);
    this.sync();
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.#onChange);
  }

  get button() {
    return this.querySelector('button');
  }

  get item() {
    const data = this.dataset;
    return {
      handle: data.handle,
      id: data.productId,
      variantId: data.variantId,
      title: data.title,
      url: data.url,
      image: data.image,
      price: data.price,
    };
  }

  sync() {
    const button = this.button;
    if (!button) return;

    const active = has(this.dataset.handle);
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    this.classList.toggle('is-active', active);

    const label = active ? this.dataset.labelRemove : this.dataset.labelAdd;
    if (label) button.setAttribute('aria-label', label);
  }
}

class WishlistCount extends HTMLElement {
  #onChange = () => this.render();

  connectedCallback() {
    if (!available) {
      this.hidden = true;
      return;
    }
    document.addEventListener(CHANGE_EVENT, this.#onChange);
    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.#onChange);
  }

  render() {
    const total = count();

    const badge = document.createElement('span');
    badge.className = 'mbb-wish-count__text';
    badge.setAttribute('aria-hidden', 'true');
    badge.textContent = total > 99 ? '99+' : String(total);

    this.replaceChildren(badge);

    const template = this.dataset.labelTemplate;
    if (template) {
      const spoken = document.createElement('span');
      spoken.className = 'visually-hidden';
      spoken.textContent = template.replace('[count]', String(total));
      // An `aria-label` on the wrapping link would otherwise swallow the badge, so the
      // header points `aria-describedby` at this id — the same trick as the cart bubble.
      if (this.dataset.labelId) spoken.id = this.dataset.labelId;
      this.append(spoken);
    }

    this.dataset.count = String(total);
    this.hidden = total === 0;
  }
}

class WishlistList extends HTMLElement {
  #onChange = () => this.render();

  connectedCallback() {
    document.addEventListener(CHANGE_EVENT, this.#onChange);

    if (this.dataset.mbbWishBound !== 'true') {
      this.dataset.mbbWishBound = 'true';
      this.addEventListener('click', (event) => this.#onClick(event));
    }

    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.#onChange);
  }

  #onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const removeButton = target.closest('[data-wishlist-remove]');
    if (removeButton) {
      event.preventDefault();
      remove(removeButton.getAttribute('data-wishlist-remove'));
      this.#say(this.dataset.labelRemoved || '');
      return;
    }

    const clearButton = target.closest('[data-wishlist-clear]');
    if (clearButton) {
      event.preventDefault();
      clear();
      return;
    }

    const addAllButton = target.closest('[data-wishlist-add-all]');
    if (addAllButton) {
      event.preventDefault();
      this.#addAll(addAllButton);
    }
  }

  #say(message) {
    const region = this.querySelector('[data-wishlist-status]');
    if (region) region.textContent = message;
  }

  async #addAll(button) {
    const lines = cache
      .filter((item) => /^\d+$/.test(item.variantId))
      .map((item) => ({ id: Number(item.variantId), quantity: 1 }));

    if (lines.length === 0) {
      this.#say(this.dataset.labelError || '');
      return;
    }

    const endpoint = `${this.dataset.cartAddUrl || '/cart/add'}.js`;
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: lines }),
      });
      if (!response.ok) throw new Error(String(response.status));
      window.location.href = this.dataset.cartUrl || '/cart';
    } catch {
      button.removeAttribute('aria-busy');
      this.#say(this.dataset.labelError || '');
    }
  }

  #card(item) {
    const li = document.createElement('li');
    li.className = 'mbb-wishp__item';
    li.dataset.handle = item.handle;

    const media = document.createElement(item.url ? 'a' : 'div');
    media.className = 'mbb-wishp__media';
    if (item.url) media.href = item.url;

    if (item.image) {
      const img = document.createElement('img');
      img.className = 'mbb-wishp__img';
      img.src = item.image;
      img.alt = item.title;
      img.loading = 'lazy';
      img.decoding = 'async';
      media.append(img);
    } else {
      const fallback = document.createElement('span');
      fallback.className = 'mbb-wishp__img mbb-wishp__img--empty';
      fallback.setAttribute('aria-hidden', 'true');
      media.append(fallback);
    }
    li.append(media);

    const body = document.createElement('div');
    body.className = 'mbb-wishp__body';

    const title = document.createElement(item.url ? 'a' : 'span');
    title.className = 'mbb-wishp__title';
    if (item.url) title.href = item.url;
    title.textContent = item.title || item.handle;
    body.append(title);

    if (item.price) {
      const price = document.createElement('span');
      price.className = 'mbb-wishp__price';
      price.textContent = item.price;
      body.append(price);
    }

    const actions = document.createElement('div');
    actions.className = 'mbb-wishp__actions';

    if (item.url) {
      const view = document.createElement('a');
      view.className = 'mbb-wishp__view';
      view.href = item.url;
      view.textContent = this.dataset.labelView || 'View';
      actions.append(view);
    }

    const removeButton = document.createElement('button');
    removeButton.type = 'button';
    removeButton.className = 'mbb-wishp__remove';
    removeButton.setAttribute('data-wishlist-remove', item.handle);
    removeButton.textContent = this.dataset.labelRemove || 'Remove';
    removeButton.setAttribute(
      'aria-label',
      `${this.dataset.labelRemove || 'Remove'}: ${item.title || item.handle}`
    );
    actions.append(removeButton);

    body.append(actions);
    li.append(body);
    return li;
  }

  render() {
    const list = this.querySelector('[data-wishlist-items]');
    const empty = this.querySelector('[data-wishlist-empty]');
    const tools = this.querySelector('[data-wishlist-tools]');
    const total = available ? count() : 0;

    if (list) {
      if (total === 0) {
        list.replaceChildren();
        list.hidden = true;
      } else {
        list.replaceChildren(...cache.map((item) => this.#card(item)));
        list.hidden = false;
      }
    }
    if (empty) empty.hidden = total !== 0;
    if (tools) tools.hidden = total === 0;

    const heading = this.querySelector('[data-wishlist-total]');
    if (heading) {
      const template = heading.dataset.labelTemplate || '';
      heading.textContent = template.replace('[count]', String(total));
      heading.hidden = total === 0;
    }
  }
}

if (!window.customElements.get('mbb-wishlist-toggle')) {
  window.customElements.define('mbb-wishlist-toggle', WishlistToggle);
}
if (!window.customElements.get('mbb-wishlist-count')) {
  window.customElements.define('mbb-wishlist-count', WishlistCount);
}
if (!window.customElements.get('mbb-wishlist-list')) {
  window.customElements.define('mbb-wishlist-list', WishlistList);
}

/* ------------------------------------------------------------------ lifecycle */

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => hydrate('load'), { once: true });
} else {
  hydrate('load');
}

// Shopify caches pages, and section swaps replace whole subtrees. Elements upgraded into
// a fresh subtree sync themselves on connect, but re-reading here keeps a stale in-memory
// cache from outliving a change made in another tab while this page sat in the cache.
document.addEventListener('shopify:section:load', () => hydrate('section-load'));
window.addEventListener('pageshow', (event) => {
  if (event.persisted) hydrate('pageshow');
});

// Another tab wrote to our key (or cleared everything, in which case key is null).
window.addEventListener('storage', (event) => {
  if (event.key !== null && event.key !== KEY) return;
  hydrate('storage');
});

window.MbbWishlist = {
  KEY,
  CHANGE_EVENT,
  SCHEMA_VERSION,
  MAX_ITEMS,
  available,
  items,
  count,
  has,
  add,
  remove,
  toggle,
  clear,
  hydrate,
};
